import React from 'react';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav, {
  getAccountSettingsNavTabs,
} from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

/**
 * Commonly used layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName add more style rules in addition to component's own css.container
 * @param {string?} props.mainColumnClassName add more style rules in addition to css.main
 * @param {string?} props.sideNavClassName add more style rules in addition to css.sideNav
 * @param {ReactNode} props.children
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.sideNav
 * @param {ReactNode?} props.footer
 * @param {boolean?} props.useAccountSettingsNav
 * @param {string?} props.currentPage
 * @returns {JSX.Element} LayoutComposer that expects children to be a function.
 */
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    accountSettingsNavProps,
    intl,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = classNames(css.container, containerClassName);

  // T9: no aside when there is nothing to put in it (account nav with a single entry)
  const showAccountSettingsNav =
    useAccountSettingsNav && getAccountSettingsNavTabs(accountSettingsNavProps).length > 1;
  const hasSideNav = !!sideNavContent || showAccountSettingsNav;

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
            </Topbar>
            <Main as="div" className={containerClasses}>
              {hasSideNav ? (
                <aside className={classNames(css.sideNav, sideNavClassName)}>
                  {showAccountSettingsNav ? (
                    <LayoutWrapperAccountSettingsSideNav
                      accountSettingsNavProps={accountSettingsNavProps}
                      ariaLabel={intl.formatMessage({
                        id: 'LayoutSideNavigation.screenreader.accountNavigation',
                      })}
                    />
                  ) : null}
                  {sideNavContent}
                </aside>
              ) : null}
              <main
                id="main-content"
                className={classNames(
                  css.main,
                  { [css.mainWithoutSideNav]: !hasSideNav },
                  mainColumnClassName
                )}
              >
                {children}
              </main>
            </Main>
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

export default LayoutSideNavigation;
